import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Table, BarChart3, Maximize2, X, Zap, Palette, Star, ChevronRight } from 'lucide-react';

const DashboardPreviews = () => {
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Memoize static data
  const previews = useMemo(() => [
    { 
      src: '/images/dashboard.png',
      alt: 'Dashboard Overview', 
      icon: <Monitor className="w-6 h-6" />,
      title: 'Command Center',
      desc: 'Real-time KPIs, lead scores, and anomaly detection in one unified view.',
      color: 'from-pink-500 to-rose-500',
      stats: ['Real-time KPIs', 'Anomaly Alerts', 'Lead Distribution']
    },
    { 
      src: '/images/aisettings.png',
      alt: 'AI Settings', 
      icon: <Zap className="w-6 h-6" />,
      title: 'AI Filtering',
      desc: 'Configure scoring rules, thresholds, and model training for smarter lead prioritization.',
      color: 'from-emerald-500 to-teal-500',
      stats: ['Scoring Rules', 'Threshold Setup', 'Model Training']
    },
    { 
      src: '/images/branding.png',
      alt: 'Branding', 
      icon: <Palette className="w-6 h-6" />,
      title: 'White Labelling',
      desc: 'Customize colors, logos, and domains to create a fully branded client experience.',
      color: 'from-violet-500 to-purple-500',
      stats: ['Custom Logo', 'Color Themes', 'Domain Setup']
    }, 
    { 
      src: '/images/feedbackreview.png',
      alt: 'Feedback Review', 
      icon: <Star className="w-6 h-6" />,
      title: 'Feedback Review',
      desc: 'Label leads for quality, provide tags, and refine AI models based on your insights.',
      color: 'from-amber-500 to-orange-500',
      stats: ['Lead Labeling', 'Quality Tags', 'AI Improvement']
    }, 
    { 
      src: '/images/leads.png',
      alt: 'Leads Table', 
      icon: <Table className="w-6 h-6" />,
      title: 'Leads Nexus',
      desc: 'Sortable, searchable leads—export, filter, and act on prospects instantly.',
      color: 'from-blue-500 to-cyan-500',
      stats: ['Sortable Tables', 'Search Filters', 'Bulk Actions']
    },  
    { 
      src: '/images/Ads.png',
      alt: 'Ads Chart', 
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Ads Matrix',
      desc: 'Visual campaign analytics—track UTM sources and conversion paths.',
      color: 'from-indigo-500 to-purple-500',
      stats: ['ROI Charts', 'UTM Tracking', 'Conversion Paths']
    },
  ], []);

  // Respect reduced motion
  const prefersReducedMotion = useMemo(() => 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches, []
  );

  const containerVariants = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Animated background - CSS where possible */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl will-change-transform"
          style={{ transform: 'translateZ(0)' }}
          animate={prefersReducedMotion ? {} : { 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl will-change-transform"
          style={{ transform: 'translateZ(0)' }}
          animate={prefersReducedMotion ? {} : { 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl will-change-transform"
          style={{ transform: 'translateZ(0)' }}
          animate={prefersReducedMotion ? {} : { 
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      {/* Static grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          transform: 'translateZ(0)',
        }} />
      </div>  

      {/* Dashboard Previews */}
      {/* Dashboard Previews */}
<motion.div
  initial={prefersReducedMotion ? {} : { opacity: 0, y: 40 }}
  animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
  className="pt-10"  // ← ADD THIS LINE
>
        <div className="text-center mb-20">
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? {} : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-6"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-sm border border-purple-500/20">
              <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
              <span className="text-sm font-semibold text-purple-300">Interactive Previews</span>
            </div>
          </motion.div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 text-white leading-[1.1] tracking-tight">
            Dashboard<br />
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Previews
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Explore every feature,click any card to expand and see the full view.
          </p>
        </div>

        {/* Grid of Preview Cards */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
        >
          {previews.map((preview, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              onHoverStart={() => setHoveredCard(idx)}
              onHoverEnd={() => setHoveredCard(null)}
              whileHover={prefersReducedMotion ? {} : { y: -12 }}
              onClick={() => setSelectedPreview(preview)}
              className="group relative cursor-pointer will-change-transform"
              style={{ transform: 'translateZ(0)' }}
            >
              <div className="relative h-full bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-md rounded-3xl p-7 border border-white/10 hover:border-white/20 transition-all duration-500 overflow-hidden flex flex-col shadow-2xl shadow-black/20">
                
                {/* Enhanced gradient overlay */}
                <motion.div 
                  className={`absolute inset-0 bg-gradient-to-br ${preview.color} opacity-0 transition-opacity duration-500 rounded-3xl`}
                  animate={{ opacity: hoveredCard === idx ? 0.15 : 0 }}
                />

                {/* Animated glow effect */}
                <motion.div
                  className={`absolute -inset-1 bg-gradient-to-r ${preview.color} opacity-0 blur-2xl transition-opacity duration-500 will-change-transform`}
                  style={{ transform: 'translateZ(0)' }}
                  animate={{ opacity: hoveredCard === idx ? 0.3 : 0 }}
                />

                <div className="relative z-10 flex flex-col h-full">
                  {/* Enhanced icon container */}
                  <motion.div
                    className={`mb-5 w-14 h-14 rounded-2xl bg-gradient-to-br ${preview.color} flex items-center justify-center shadow-xl will-change-transform`}
                    style={{ transform: 'translateZ(0)' }}
                    whileHover={prefersReducedMotion ? {} : { rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="text-white">
                      {preview.icon}
                    </div>
                  </motion.div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-bold mb-3 text-white leading-tight">
                    {preview.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5 flex-grow">
                    {preview.desc}
                  </p>

                  {/* Refined stats badges */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {preview.stats.map((stat, sIdx) => (
                      <motion.span
                        key={sIdx}
                        initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.8 }}
                        animate={prefersReducedMotion ? {} : { opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.05 * sIdx, duration: 0.3 }}
                        whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                        className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/10 text-slate-300 text-xs font-medium border border-white/10 hover:border-white/20 transition-all duration-300 will-change-transform"
                        style={{ transform: 'translateZ(0)' }}
                      >
                        {stat}
                      </motion.span>
                    ))}
                  </div>

                  {/* Enhanced image preview */}
                  <motion.div
                    className="relative overflow-hidden rounded-2xl shadow-2xl group/img bg-slate-950/50 will-change-transform"
                    style={{ transform: 'translateZ(0)' }}
                    whileHover={prefersReducedMotion ? {} : { scale: 1.03 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    <img 
                      src={preview.src} 
                      alt={preview.alt} 
                      className="w-full h-48 object-cover transition-all duration-700 group-hover/img:scale-110 group-hover/img:brightness-110" 
                    />
                    
                    {/* Enhanced overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center">
                      <motion.div
                        initial={prefersReducedMotion ? {} : { scale: 0, rotate: -180 }}
                        whileHover={prefersReducedMotion ? {} : { scale: 1, rotate: 0 }}
                        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                        className={`w-16 h-16 bg-gradient-to-br ${preview.color} backdrop-blur-sm rounded-2xl flex items-center justify-center border-2 border-white/20 shadow-2xl will-change-transform`}
                        style={{ transform: 'translateZ(0)' }}
                      >
                        <Maximize2 className="w-7 h-7 text-white" />
                      </motion.div>
                    </div>

                    {/* Smooth animated border */}
                    <motion.div 
                      className={`absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500`}
                      animate={{ opacity: hoveredCard === idx ? 1 : 0 }}
                      style={{
                        background: `linear-gradient(to right, ${preview.color.replace('from-', '').replace('to-', ', ')})`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        padding: '2px'
                      }}
                    />
                  </motion.div>

                  {/* Smooth bottom accent */}
                  <motion.div
                    className={`mt-6 h-1.5 bg-gradient-to-r ${preview.color} rounded-full`}
                    initial={prefersReducedMotion ? {} : { scaleX: 0, opacity: 0 }}
                    animate={prefersReducedMotion ? {} : { 
                      scaleX: hoveredCard === idx ? 1 : 0,
                      opacity: hoveredCard === idx ? 1 : 0
                    }}
                    transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                    style={{ transformOrigin: 'left' }}
                  />

                  {/* View details indicator */}
                  <motion.div
                    className="flex items-center gap-2 mt-4 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    animate={prefersReducedMotion ? {} : { x: hoveredCard === idx ? 0 : -10 }}
                  >
                    <span className="text-slate-300">View details</span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced bottom badge */}
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-4 px-8 py-4 rounded-3xl bg-gradient-to-r from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-500 shadow-xl">
            <div className="flex -space-x-3">
              {previews.map((preview, idx) => (
                <motion.div
                  key={idx}
                  initial={prefersReducedMotion ? {} : { scale: 0, opacity: 0 }}
                  animate={prefersReducedMotion ? {} : { scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05, duration: 0.3 }}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.2, zIndex: 10 }}
                  className={`w-10 h-10 rounded-full bg-gradient-to-br ${preview.color} border-2 border-slate-900 shadow-lg will-change-transform`}
                  style={{ transform: 'translateZ(0)' }}
                />
              ))}
            </div>
            <p className="text-slate-300 text-sm font-medium">
              Built with React — real-time backend integration
            </p>
          </div>
        </motion.div>
      </motion.div>

      {/* Enhanced Lightbox Modal */}
      <AnimatePresence>
        {selectedPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelectedPreview(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/98 backdrop-blur-2xl p-4 md:p-8"
          >
            <motion.div
              initial={prefersReducedMotion ? {} : { scale: 0.85, opacity: 0, y: 40 }}
              animate={prefersReducedMotion ? {} : { scale: 1, opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? {} : { scale: 0.85, opacity: 0, y: 40 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-6xl w-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl overflow-hidden shadow-2xl"
            >
              {/* Enhanced close button */}
              <motion.button
                onClick={() => setSelectedPreview(null)}
                whileHover={prefersReducedMotion ? {} : { scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute top-6 right-6 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white hover:bg-white/20 border border-white/10 hover:border-white/20 transition-all duration-300 shadow-xl will-change-transform"
                style={{ transform: 'translateZ(0)' }}
              >
                <X className="w-6 h-6" />
              </motion.button>

              {/* Modal content */}
              <div className="p-8 md:p-12">
                <motion.div 
                  initial={prefersReducedMotion ? {} : { scale: 0, rotate: -180 }}
                  animate={prefersReducedMotion ? {} : { scale: 1, rotate: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${selectedPreview.color} shadow-2xl mb-6 will-change-transform`}
                  style={{ transform: 'translateZ(0)' }}
                >
                  <div className="text-white text-2xl">
                    {selectedPreview.icon}
                  </div>
                </motion.div>
                
                <motion.h3 
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight"
                >
                  {selectedPreview.title}
                </motion.h3>
                
                <motion.p 
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="text-slate-300 text-lg mb-8 leading-relaxed max-w-3xl"
                >
                  {selectedPreview.desc}
                </motion.p>

                <motion.div 
                  initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="bg-slate-950/70 rounded-2xl p-4 border border-white/10 mb-6 overflow-hidden"
                >
                  <img
                    src={selectedPreview.src}
                    alt={selectedPreview.alt}
                    className="w-full max-h-[65vh] object-contain rounded-xl"
                  />
                </motion.div>

                <motion.div 
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="flex flex-wrap gap-3"
                >
                  {selectedPreview.stats.map((stat, sIdx) => (
                    <motion.span
                      key={sIdx}
                      initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.8 }}
                      animate={prefersReducedMotion ? {} : { opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 + (sIdx * 0.05) }}
                      whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                      className="text-sm px-4 py-2 rounded-full bg-white/10 text-slate-300 border border-white/10 hover:border-white/20 transition-all duration-300 font-medium will-change-transform"
                      style={{ transform: 'translateZ(0)' }}
                    >
                      {stat}
                    </motion.span>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default DashboardPreviews;